/**
 * Editable duration cell — a "5d" pill that swaps to a number input on click.
 * Enter / blur commits (whole workdays >= 1); Escape cancels.
 */
import React from "react";
import "./DurationCell.css";

const DurationCell: React.FC<{
  days: number;
  edited: boolean;
  disabled: boolean;
  onCommit: (days: number) => void;
}> = ({ days, edited, disabled, onCommit }) => {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const shown = Number.isInteger(days) ? String(days) : days.toFixed(1);

  if (disabled) {
    return <span className="cwb-dur-locked">{days > 0 ? `${shown}d` : "—"}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={`cwb-dur-btn${edited ? " is-edited" : ""}`}
        onClick={() => {
          setValue(shown);
          setEditing(true);
        }}
        title="Edit duration (workdays)"
      >
        {shown}d
      </button>
    );
  }

  const commit = () => {
    setEditing(false);
    const parsed = Math.round(Number(value));
    if (!Number.isFinite(parsed) || parsed < 1 || parsed === days) return;
    onCommit(parsed);
  };

  return (
    <input
      ref={inputRef}
      type="number"
      className="cwb-dur-input"
      min={1}
      step={1}
      value={value}
      autoFocus
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setEditing(false);
      }}
    />
  );
};

export default DurationCell;
